import { ContactType } from "../models/Contact";

export type FeatureLevel = 0 | 1 | 2;

export const FEATURE_IDS = [
  "dashboard",
  "chat",
  "leads",
  "accounts",
  "vehicles",
  "opsManagement",
  "dismantling",
  "parts",
  "orderManagement",
  "widget",
  "admin",
  "findLead",
  "pricing",
] as const;

export type FeatureId = (typeof FEATURE_IDS)[number];

export type FeatureScopes = Record<FeatureId, FeatureLevel>;

export const FEATURE_CLAIM_PREFIX = "feat.";
/** Guid of a customer-defined CustomerRole, or "custom" when scopes were hand-edited. */
export const DASHBOARD_ROLE_CLAIM = "dashboardRoleId";

export const FEATURE_LABELS: Record<FeatureId, string> = {
  dashboard: "Dashboard",
  chat: "Team Chat",
  leads: "Leads",
  accounts: "Accounts",
  vehicles: "Vehicles",
  opsManagement: "Ops Management",
  dismantling: "Dismantling",
  parts: "Parts",
  orderManagement: "Order Management",
  widget: "Widget",
  admin: "Admin",
  findLead: "Find Lead",
  pricing: "Vehicle Pricing",
};

export const LEVEL_OPTIONS: { value: FeatureLevel; label: string }[] = [
  { value: 0, label: "None" },
  { value: 1, label: "Read" },
  { value: 2, label: "Write" },
];

// None/Read/Write reads badly for pricing, and full control is the default so
// it sits first.
export const PRICING_LEVEL_OPTIONS: { value: FeatureLevel; label: string }[] = [
  { value: 2, label: "Full control" },
  { value: 1, label: "View only" },
  { value: 0, label: "Hidden" },
];

export function levelOptionsFor(feature: FeatureId) {
  return feature === "pricing" ? PRICING_LEVEL_OPTIONS : LEVEL_OPTIONS;
}

/**
 * Features that mean "full access" when absent from claims or a stored role.
 * Existing JWTs and CustomerRole rows predate these, so a missing key must not
 * read as level 0 — that would strip access from everyone the day it ships.
 * Same reasoning as inheritVehicleSplitScopes.
 */
const DEFAULT_FULL_FEATURES: FeatureId[] = ["pricing"];

/**
 * Fill in DEFAULT_FULL_FEATURES that the source didn't mention. `present` must
 * report whether the source carried a usable level, not whether the key existed
 * at all — a deliberate level 0 has to survive this, but an unparseable value has
 * to fall open the way the API's PricingScopeRules.ResolveLevel does. If the two
 * ends disagree the UI hides prices the API is still willing to serve.
 */
export function applyDefaultScopes(
  scopes: FeatureScopes,
  present: (feature: FeatureId) => boolean
): FeatureScopes {
  const result = { ...scopes };
  for (const id of DEFAULT_FULL_FEATURES) {
    if (!present(id)) result[id] = 2;
  }
  return result;
}

/** Features hidden from the role editor when TME is enabled (not on the TME sidebar). */
const TME_HIDDEN_FEATURES: FeatureId[] = ["leads", "widget", "findLead"];
/** Features only shown in the role editor when TME is enabled (TME sidebar split of Vehicles). */
const TME_ONLY_FEATURES: FeatureId[] = ["opsManagement", "dismantling"];

/** Feature ids shown in the role / staff editor for the current sidebar layout. */
export function visibleFeatureIds(isTme: boolean): FeatureId[] {
  if (isTme) {
    return FEATURE_IDS.filter((id) => !TME_HIDDEN_FEATURES.includes(id));
  }
  return FEATURE_IDS.filter((id) => !TME_ONLY_FEATURES.includes(id));
}

/**
 * Keep opsManagement / dismantling in sync with vehicles when TME is off so
 * enabling TME later preserves access without a role migration.
 */
export function syncVehicleSplitScopes(
  scopes: FeatureScopes,
  isTme: boolean
): FeatureScopes {
  if (isTme) return scopes;
  return {
    ...scopes,
    opsManagement: scopes.vehicles,
    dismantling: scopes.vehicles,
  };
}

/** Older roles / JWTs only had vehicles — inherit until explicitly set. */
function inheritVehicleSplitScopes(
  scopes: FeatureScopes,
  hasOpsManagement: boolean,
  hasDismantling: boolean
): FeatureScopes {
  return {
    ...scopes,
    opsManagement: hasOpsManagement ? scopes.opsManagement : scopes.vehicles,
    dismantling: hasDismantling ? scopes.dismantling : scopes.vehicles,
  };
}

export function featureClaimKey(feature: FeatureId): string {
  return `${FEATURE_CLAIM_PREFIX}${feature}`;
}

export function emptyFeatureScopes(level: FeatureLevel = 0): FeatureScopes {
  return FEATURE_IDS.reduce((acc, id) => {
    acc[id] = level;
    return acc;
  }, {} as FeatureScopes);
}

export function allWriteScopes(): FeatureScopes {
  return emptyFeatureScopes(2);
}

export type DashboardRoleId =
  | "admin"
  | "staff"
  | "mechanic"
  | "yard"
  | "office"
  | "driver"
  | "exporter"
  | "custom";

export interface RoleTemplate {
  id: DashboardRoleId;
  label: string;
  contactType: ContactType;
  scopes: FeatureScopes;
}

const staffScopes = (): FeatureScopes => ({
  ...emptyFeatureScopes(0),
  dashboard: 2,
  chat: 2,
  leads: 2,
  accounts: 2,
  vehicles: 2,
  opsManagement: 2,
  dismantling: 2,
  parts: 2,
  orderManagement: 2,
  widget: 2,
  pricing: 2,
});

export const ROLE_TEMPLATES: RoleTemplate[] = [
  {
    id: "admin",
    label: "Admin",
    contactType: ContactType.Admin,
    scopes: allWriteScopes(),
  },
  {
    id: "staff",
    label: "Staff",
    contactType: ContactType.Staff,
    scopes: staffScopes(),
  },
  {
    id: "mechanic",
    label: "Mechanic",
    contactType: ContactType.Mechanic,
    scopes: staffScopes(),
  },
  {
    id: "yard",
    label: "Yard",
    contactType: ContactType.Staff,
    scopes: {
      ...emptyFeatureScopes(0),
      dashboard: 2,
      chat: 2,
      vehicles: 2,
      opsManagement: 2,
      dismantling: 2,
      parts: 2,
      pricing: 2,
    },
  },
  {
    id: "office",
    label: "Office",
    contactType: ContactType.Staff,
    scopes: {
      ...emptyFeatureScopes(0),
      dashboard: 2,
      chat: 2,
      leads: 2,
      accounts: 2,
      orderManagement: 2,
      widget: 2,
      pricing: 2,
    },
  },
  {
    id: "driver",
    label: "Driver",
    contactType: ContactType.Driver,
    scopes: {
      ...emptyFeatureScopes(0),
      findLead: 2,
      // Drivers are shown the price and commission on collection so they know
      // what to hand over — see DriverPaymentDetails.
      pricing: 2,
    },
  },
  {
    id: "exporter",
    label: "Exporter",
    contactType: ContactType.Exporter,
    scopes: {
      ...emptyFeatureScopes(0),
      parts: 2,
      pricing: 2,
    },
  },
];

export function getRoleTemplate(id: DashboardRoleId): RoleTemplate | undefined {
  return ROLE_TEMPLATES.find((t) => t.id === id);
}

export interface RoleFlags {
  isAdmin: boolean;
  isStaff: boolean;
  isDriver: boolean;
  isMechanic: boolean;
  isExporter: boolean;
}

/** Flat claim map as it appears on the JWT / UserClaims. */
export type ClaimMap = Record<string, string | undefined>;

export function parseFeatureLevel(value: string | undefined): FeatureLevel {
  if (value === "1") return 1;
  if (value === "2") return 2;
  return 0;
}

/**
 * Whether a claim carries a level we can actually read. Mirrors int.TryParse plus
 * the range check in PricingScopeRules.ResolveLevel: blank, junk and out-of-range
 * all mean "the source said nothing", so fail-open features stay open.
 */
export function hasUsableFeatureLevel(value: string | undefined): boolean {
  return value === "0" || value === "1" || value === "2";
}

export function hasExplicitFeatureClaims(claims: ClaimMap): boolean {
  return FEATURE_IDS.some((id) => {
    const value = claims[featureClaimKey(id)];
    return value !== undefined && value !== "";
  });
}

export function legacyFeatureScopes(flags: RoleFlags): FeatureScopes {
  if (flags.isAdmin) return allWriteScopes();
  if (flags.isDriver) {
    return { ...emptyFeatureScopes(0), findLead: 2, pricing: 2 };
  }
  if (flags.isExporter) {
    return { ...emptyFeatureScopes(0), parts: 2, pricing: 2 };
  }
  if (flags.isStaff || flags.isMechanic) {
    return staffScopes();
  }
  return emptyFeatureScopes(0);
}

export function parseFeatureScopesFromClaims(claims: ClaimMap): FeatureScopes {
  const scopes = emptyFeatureScopes(0);
  for (const id of FEATURE_IDS) {
    scopes[id] = parseFeatureLevel(claims[featureClaimKey(id)]);
  }
  const claimPresent = (id: FeatureId) =>
    hasUsableFeatureLevel(claims[featureClaimKey(id)]);
  const opsKey = featureClaimKey("opsManagement");
  const dismantlingKey = featureClaimKey("dismantling");
  return applyDefaultScopes(
    inheritVehicleSplitScopes(
      scopes,
      claims[opsKey] !== undefined && claims[opsKey] !== "",
      claims[dismantlingKey] !== undefined && claims[dismantlingKey] !== ""
    ),
    claimPresent
  );
}

/**
 * Resolve effective feature scopes from JWT/user claims.
 * Admins always get full write access. Users without any feat.* claims
 * fall back to the legacy role-flag mapping.
 */
export function resolveFeatureScopes(
  claims: ClaimMap,
  flags: RoleFlags
): FeatureScopes {
  if (flags.isAdmin) return allWriteScopes();
  if (hasExplicitFeatureClaims(claims)) {
    return parseFeatureScopesFromClaims(claims);
  }
  return legacyFeatureScopes(flags);
}

export function canAccessFeature(
  scopes: FeatureScopes | undefined,
  feature: FeatureId,
  minLevel: FeatureLevel = 1
): boolean {
  if (!scopes) return false;
  return scopes[feature] >= minLevel;
}

/** Map sidebar group label (English source key) or path to a feature id. */
export function featureForNavGroup(group: string | undefined, to: string): FeatureId | null {
  if (!group) {
    if (to === "/" || to === "") return "dashboard";
    if (to === "chat") return "chat";
    if (to === "find-lead") return "findLead";
    if (to === "exports") return "parts";
    return null;
  }

  switch (group) {
    case "Leads":
      return "leads";
    case "Accounts":
      return "accounts";
    case "Vehicles":
      return "vehicles";
    case "Ops Management":
      return "opsManagement";
    case "Dismantling":
      return "dismantling";
    case "Parts":
      return "parts";
    case "Order Management":
      return "orderManagement";
    case "Widget":
      return "widget";
    case "Admin":
      // Find Lead sits in Admin nav but uses its own scope.
      if (to === "find-lead") return "findLead";
      return "admin";
    case "Debug":
      return "admin";
    default:
      return null;
  }
}

/**
 * Longest-prefix match of path segment(s) to a feature.
 * Path should be without leading slash, e.g. "leads/abc" or "".
 */
export const ROUTE_FEATURE_MAP: { prefix: string; feature: FeatureId }[] = [
  { prefix: "", feature: "dashboard" },
  { prefix: "chat", feature: "chat" },
  { prefix: "quotes", feature: "leads" },
  { prefix: "bookings", feature: "leads" },
  { prefix: "leads", feature: "leads" },
  { prefix: "collections-board", feature: "leads" },
  { prefix: "vehicles-collected", feature: "leads" },
  { prefix: "calendar", feature: "leads" },
  { prefix: "trip-manager", feature: "leads" },
  { prefix: "old-trip-manager", feature: "leads" },
  { prefix: "drop-offs", feature: "leads" },
  // Payments is the vehicle purchase ledger (what we pay the seller), so it hangs
  // off pricing rather than leads — a role can lose it without losing leads.
  // Invoices stays on accounts: those are part sales to customers, not purchases.
  { prefix: "payments", feature: "pricing" },
  { prefix: "schedule", feature: "leads" },
  { prefix: "accounts", feature: "accounts" },
  { prefix: "invoices", feature: "accounts" },
  { prefix: "vehicles", feature: "vehicles" },
  { prefix: "vehicle-board", feature: "vehicles" },
  { prefix: "parts-manager", feature: "vehicles" },
  { prefix: "depollution", feature: "vehicles" },
  { prefix: "triage", feature: "vehicles" },
  { prefix: "jobs", feature: "vehicles" },
  { prefix: "production-planner", feature: "vehicles" },
  { prefix: "production-dashboard", feature: "vehicles" },
  { prefix: "station-manager", feature: "vehicles" },
  { prefix: "work-orders", feature: "parts" },
  { prefix: "exports", feature: "parts" },
  { prefix: "awaiting-arrival", feature: "parts" },
  { prefix: "parts-checker", feature: "parts" },
  { prefix: "stock", feature: "parts" },
  { prefix: "core", feature: "parts" },
  { prefix: "stock-qc", feature: "parts" },
  { prefix: "ebay-unlisted", feature: "orderManagement" },
  { prefix: "recently-listed", feature: "orderManagement" },
  { prefix: "ebay-listings", feature: "orderManagement" },
  { prefix: "ebay-optimisation", feature: "orderManagement" },
  { prefix: "ebay-orders", feature: "orderManagement" },
  { prefix: "ebay-returns", feature: "orderManagement" },
  { prefix: "widget-dashboard", feature: "widget" },
  { prefix: "widget-calculator", feature: "widget" },
  { prefix: "widget-postcodes", feature: "widget" },
  { prefix: "widget-suppliers", feature: "widget" },
  { prefix: "sellavehicle", feature: "widget" },
  { prefix: "abandoned-quotes", feature: "widget" },
  { prefix: "widget-config", feature: "widget" },
  { prefix: "admin", feature: "admin" },
  { prefix: "parts-editor", feature: "admin" },
  { prefix: "ebay-messages", feature: "admin" },
  { prefix: "partpro-messages", feature: "admin" },
  { prefix: "category-editor", feature: "admin" },
  { prefix: "category-images", feature: "admin" },
  { prefix: "reports", feature: "admin" },
  { prefix: "performance", feature: "admin" },
  // "tickets" is deliberately unmapped — support tickets stay open to everyone.
  { prefix: "driver-tracking", feature: "admin" },
  { prefix: "test", feature: "admin" },
  { prefix: "debug", feature: "admin" },
  { prefix: "find-lead", feature: "findLead" },
  { prefix: "driver", feature: "findLead" },
  { prefix: "settings", feature: "admin" },
  { prefix: "contacts", feature: "accounts" },
  { prefix: "search", feature: "dashboard" },
];

/** TME moves some Vehicles / Leads routes into Ops Management or Dismantling. */
const TME_ROUTE_FEATURE_OVERRIDES: { prefix: string; feature: FeatureId }[] = [
  { prefix: "drop-offs", feature: "opsManagement" },
  { prefix: "vehicle-board", feature: "opsManagement" },
  { prefix: "production-planner", feature: "opsManagement" },
  { prefix: "production-dashboard", feature: "opsManagement" },
  { prefix: "depollution", feature: "dismantling" },
  { prefix: "triage", feature: "dismantling" },
  { prefix: "station-manager", feature: "dismantling" },
];

function matchRouteFeature(
  normalised: string,
  map: { prefix: string; feature: FeatureId }[]
): FeatureId | null {
  const sorted = [...map].sort((a, b) => b.prefix.length - a.prefix.length);
  for (const entry of sorted) {
    if (entry.prefix === "") {
      if (normalised === "") return entry.feature;
      continue;
    }
    if (
      normalised === entry.prefix ||
      normalised.startsWith(`${entry.prefix}/`)
    ) {
      return entry.feature;
    }
  }
  return null;
}

export function featureForRoutePath(
  pathname: string,
  isTme = false
): FeatureId | null {
  const normalised = pathname.replace(/^\//, "").replace(/\/$/, "");
  if (isTme) {
    const tmeFeature = matchRouteFeature(normalised, TME_ROUTE_FEATURE_OVERRIDES);
    if (tmeFeature) return tmeFeature;
  }
  return matchRouteFeature(normalised, ROUTE_FEATURE_MAP);
}

export function contactTypeToRoleFlags(type: ContactType): RoleFlags {
  return {
    isAdmin: type === ContactType.Admin,
    isStaff: type === ContactType.Staff,
    isDriver: type === ContactType.Driver,
    isMechanic: type === ContactType.Mechanic,
    isExporter: type === ContactType.Exporter,
  };
}

export function roleFlagsToContactType(flags: RoleFlags): ContactType | undefined {
  if (flags.isAdmin) return ContactType.Admin;
  if (flags.isDriver) return ContactType.Driver;
  if (flags.isStaff) return ContactType.Staff;
  if (flags.isMechanic) return ContactType.Mechanic;
  if (flags.isExporter) return ContactType.Exporter;
  return undefined;
}

export function roleFlagsToClaims(flags: RoleFlags): Record<string, string> {
  return {
    isAdmin: flags.isAdmin ? "true" : "false",
    isStaff: flags.isStaff ? "true" : "false",
    isDriver: flags.isDriver ? "true" : "false",
    isMechanic: flags.isMechanic ? "true" : "false",
    isExporter: flags.isExporter ? "true" : "false",
  };
}

export function featureScopesToClaims(scopes: FeatureScopes): Record<string, string> {
  return FEATURE_IDS.reduce((acc, id) => {
    acc[featureClaimKey(id)] = String(scopes[id]);
    return acc;
  }, {} as Record<string, string>);
}

export function buildStaffClaims(
  flags: RoleFlags,
  scopes: FeatureScopes,
  dashboardRoleId: string
): Record<string, string> {
  return {
    ...roleFlagsToClaims(flags),
    ...featureScopesToClaims(scopes),
    [DASHBOARD_ROLE_CLAIM]: dashboardRoleId,
  };
}

export function scopesFromRecord(
  record: Record<string, number> | undefined
): FeatureScopes {
  const scopes = emptyFeatureScopes(0);
  if (!record) return applyDefaultScopes(scopes, () => false);
  for (const id of FEATURE_IDS) {
    const level = record[id];
    if (level === 1 || level === 2) scopes[id] = level;
  }
  return applyDefaultScopes(
    inheritVehicleSplitScopes(
      scopes,
      record.opsManagement !== undefined,
      record.dismantling !== undefined
    ),
    // Same rule as the claim path: a stored value outside 0-2 is not a level.
    (id) => record[id] === 0 || record[id] === 1 || record[id] === 2
  );
}

export function scopesMatch(
  a: FeatureScopes,
  b: FeatureScopes
): boolean {
  return FEATURE_IDS.every((id) => a[id] === b[id]);
}

/** Returns the customer role id from claims, or "custom". */
export function detectAssignedRoleId(claimMap: ClaimMap): string {
  const stored = claimMap[DASHBOARD_ROLE_CLAIM];
  if (!stored || stored === "custom") return "custom";
  return stored;
}

export function parseScopesFromClaimMap(claimMap: ClaimMap): FeatureScopes {
  return parseFeatureScopesFromClaims(claimMap);
}

export function userClaimsToMap(
  userClaims: { type: string; value: string }[] | undefined
): ClaimMap {
  const map: ClaimMap = {};
  (userClaims ?? []).forEach((c) => {
    map[c.type] = c.value;
  });
  return map;
}

export function claimMapToRoleFlags(claims: ClaimMap): RoleFlags {
  return {
    isAdmin: claims.isAdmin === "true",
    isStaff: claims.isStaff === "true",
    isDriver: claims.isDriver === "true",
    isMechanic: claims.isMechanic === "true",
    isExporter: claims.isExporter === "true",
  };
}

/** Index signature friendly view of a decoded JWT for resolveFeatureScopes. */
export function tokenPayloadToClaimMap(
  payload: Record<string, unknown>
): ClaimMap {
  const map: ClaimMap = {};
  for (const [key, value] of Object.entries(payload)) {
    if (typeof value === "string") map[key] = value;
    else if (typeof value === "boolean" || typeof value === "number") {
      map[key] = String(value);
    }
  }
  return map;
}
